import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useState,
} from "react"

import * as p from "@shared/components/layerPopups"
import {
  getDisplay,
  getFont,
  getManagerFontColor,
  getManagerFontIsBold,
  getManagerFontSize,
} from "@shared/modules"
import { LayerPopup } from "@shared/types"
import * as types from "@shared/types/layerPopup"
import image from "../components/layerPopups/Image";

interface Props {
  children: React.ReactNode
}

const LayerPopupProvider = ({ children }: Props) => {
  const [isDimmed, setIsDimmed] = useState<boolean>(false)
  const [active, setActive] = useState<types.PopupKind | undefined>()
  const [state, dispatch] = useReducer(popupReducer, initState)

  const handleFont = useCallback(
    (font: types.Font) => {
      dispatch({ type: "CHANGE_FONT", payload: font })
    },
    [dispatch],
  )

  const handleStock = useCallback(
    (stock: types.Stock) => {
      dispatch({ type: "PICK_STOCK", payload: stock })
    },
    [dispatch],
  )

  const handleVideo = useCallback(
    (video: types.Video) => {
      dispatch({ type: "CHANGE_VIDEO_OPTION", payload: video })
    },
    [dispatch],
  )

  const handleAlert = useCallback(
    (alert: types.Alert) => {
      dispatch({ type: "CHANGE_ALERT_OPTION", payload: alert })
    },
    [dispatch],
  )

  const handleChat = useCallback(
    (chat: types.Chat) => {
      dispatch({ type: "CHANGE_CHAT_OPTION", payload: chat })
    },
    [dispatch],
  )

  const handleManagerFont = useCallback(
    (managerFont: types.ManagerFont) => {
      dispatch({ type: "CHANGE_MANAGER_FONT", payload: managerFont })
    },
    [dispatch],
  )

  const handleConfirm = useCallback(
    (confirm: types.Confirm) => {
      dispatch({ type: "CHANGE_CONFIRM", payload: confirm })
    },
    [dispatch],
  )

  const handleShare = useCallback(
    (share: types.Share) => {
      dispatch({ type: "CHANGE_SHARE", payload: share })
    },
    [dispatch],
  )

  const handleImage = useCallback(
    (image: types.Image) => {
      dispatch({ type: "CHANGE_IMAGE", payload: image })
    },
    [dispatch],
  )

  const handlePreviewLogin = useCallback(
    (previewLogin: types.PreviewLogin) => {
      dispatch({ type: "CHANGE_PREVIEW_LOGIN", payload: previewLogin })
    },
    [dispatch],
  )

  const open = useCallback(
    (active: types.PopupKind | undefined, isNotDimmed?: boolean) => {
      setActive(active)
      !isNotDimmed && setIsDimmed(true)
    },
    [],
  )

  const close = useCallback(() => {
    setActive(undefined)
    setIsDimmed(false)
  }, [])

  const { stock, font, video, alert, chat, managerFont, confirm, share, image, previewLogin } = state

  const options: LayerPopup = {
    active,
    font: {
      size: font.size,
      handleFont,
    },
    stock: {
      shortCode: stock.shortCode,
      name: stock.name,
      handleStock,
    },
    video: {
      size: video.size,
      handleVideo,
    },
    alert: {
      contents: alert.contents,
      handleAlert,
    },
    chat: {
      emoticon: chat.emoticon,
      handleChat,
    },
    managerFont: {
      size: managerFont.size,
      color: managerFont.color,
      isBold: managerFont.isBold,
      handleManagerFont,
    },
    confirm: {
      key: confirm.key,
      message: confirm.message,
      isConfirm: confirm.isConfirm,
      handleConfirm,
      confirmAction: confirm.confirmAction,
    },
    share: {
      url: share.url,
      title: share.title,
      handleShare,
    },
    image: {
      url: image.url,
      handleImage,
    },
    previewLogin: {
      message: previewLogin.message,
      buttonText: previewLogin.buttonText,
      handlePreviewLogin,
    },
    open,
    close,
  }

  return (
    <layerPopupContext.Provider value={options}>
      {isDimmed && <div className="dimmed" onClick={close} />}
      {(() => {
        switch (active) {
          case "ALERT": {
            return <p.Alert />
          }
          case "CONFIRM": {
            return <p.Confirm />
          }
          case "STOCK_SEARCH": {
            return <p.StockSearch />
          }
          case "SETTING": {
            return <p.SettingLayerPopup />
          }
          case "EMOTICON": {
            return <p.Emoticon />
          }
          case "LIVE_END": {
            return <p.LiveEnd />
          }
          case "LIVE_END_VOD": {
            return <p.LiveEndVod />
          }
          case "FONT_SETTING": {
            return <p.FontSetting />
          }
          case "SIGN_UP": {
            return <p.SignUp />
          }
          case "SUBSCRIBE_INFO": {
            return <p.SubscribeInfo />
          }
          case "PARTNER_QUESTION": {
            return <p.PartnerQuestion />
          }
          case "PARTNER_INFO": {
            return <p.PartnerInfo />
          }
          case "ANONYMOUS": {
            return <p.Anonymous />
          }
          case "PREVIEW_LOGIN": {
            return <p.PreviewLogin />
          }
          case "MANAGER_FONT_SETTING": {
            return <p.ManagerFontSetting />
          }
          case "INSTALL_CHROME": {
            return <p.InstallChrome />
          }
          case "SHARE": {
            return <p.BroadcastShare />
          }
          case "INVESTMENT_PRECAUTIONS": {
            return <p.InvestmentPrecautions />
          }
          case "IMAGE": {
            return <p.Image />
          }
        }
      })()}
      {children}
    </layerPopupContext.Provider>
  )
}

const useLayerPopupContext = () => useContext(layerPopupContext)

export { LayerPopupProvider, useLayerPopupContext }

function popupReducer(state: PopupState, action: PopupAction): PopupState {
  switch (action.type) {
    case "CHANGE_FONT": {
      return { ...state, font: action.payload as types.Font }
    }
    case "PICK_STOCK": {
      return { ...state, stock: action.payload as types.Stock }
    }
    case "CHANGE_VIDEO_OPTION": {
      return { ...state, video: action.payload as types.Video }
    }
    case "CHANGE_ALERT_OPTION": {
      return { ...state, alert: action.payload as types.Alert }
    }
    case "CHANGE_CHAT_OPTION": {
      return { ...state, chat: action.payload as types.Chat }
    }
    case "CHANGE_MANAGER_FONT": {
      return { ...state, managerFont: action.payload as types.ManagerFont }
    }
    case "CHANGE_CONFIRM": {
      return { ...state, confirm: action.payload as types.Confirm }
    }
    case "CHANGE_SHARE": {
      return { ...state, share: action.payload as types.Share }
    }
    case "CHANGE_IMAGE": {
      return { ...state, image: action.payload as types.Image }
    }
    case "CHANGE_PREVIEW_LOGIN": {
      return { ...state, previewLogin: action.payload as types.PreviewLogin }
    }
    default:
      throw new Error()
  }
}

const layerPopupContext = createContext<LayerPopup>({
  active: undefined,
  font: {
    size: "medium",
    handleFont: (font: types.Font): void => {},
  },
  stock: {
    name: "",
    shortCode: "",
    handleStock: (stock: types.Stock): void => {},
  },
  video: {
    size: "originalRatio",
    handleVideo: (video: types.Video): void => {},
  },
  alert: {
    contents: "",
    handleAlert: (alert: types.Alert): void => {},
  },
  chat: {
    emoticon: "",
    handleChat: (chat: types.Chat): void => {},
  },
  managerFont: {
    size: "medium",
    color: "",
    isBold: false,
    handleManagerFont: (managerFont: types.ManagerFont): void => {},
  },
  confirm: {
    key: "",
    message: "",
    isConfirm: false,
    handleConfirm: (confirm: types.Confirm): void => {},
    confirmAction: undefined,
  },
  share: {
    url: "",
    title: "",
    handleShare: (share: types.Share): void => {},
  },
  image: {
    url: "",
    handleImage: (url: types.Image): void => {},
  },
  previewLogin: {
    message: "",
    buttonText: "",
    handlePreviewLogin: (previewLogin: types.PreviewLogin): void => {},
  },
  open: (active: types.PopupKind | undefined) => {},
  close: () => {},
})

const initState = {
  active: undefined,
  font: {
    size: getFont() || "medium",
  },
  stock: {
    name: "",
    shortCode: "",
  },
  video: {
    size: getDisplay() || "originalRatio",
  },
  alert: {
    contents: "",
  },
  chat: {
    emoticon: "",
  },
  managerFont: {
    isBold: getManagerFontIsBold(),
    size: getManagerFontSize() || "medium",
    color: getManagerFontColor(),
  },
  confirm: {
    key: "",
    message: "",
    isConfirm: false,
  },
  share: {
    url: "",
    title: "",
  },
  image: {
    url: "",
  },
  previewLogin: {
    message: "",
    buttonText: "",
  }
}

interface PopupState {
  font: types.Font
  stock: types.Stock
  video: types.Video
  alert: types.Alert
  chat: types.Chat
  managerFont: types.ManagerFont
  confirm: types.Confirm
  share: types.Share
  image: types.Image
  previewLogin: types.PreviewLogin
}

interface PopupAction {
  type: string
  payload:
    | types.Stock
    | types.Video
    | types.Font
    | types.Alert
    | types.Chat
    | types.ManagerFont
    | types.Confirm
    | types.Share
    | types.Image
    | types.PreviewLogin
}
